import { Dialog } from "@mui/material";
import type { ReactNode } from "react";
import { Icon } from "@/components/icons";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Optional Material Symbols icon name. When set, renders the IntroHeader
   *  layout — dark icon block next to the title/description. */
  iconName?: string;
  /** Width in px. Defaults to 480. */
  width?: number;
  /** Inner padding in px. Defaults to 32 (popup utility default). */
  padding?: 24 | 32;
  /**
   * Px reserved on the left (e.g. the app sidebar's live width) — the dialog
   * centers within the remaining space instead of the full viewport, so it
   * doesn't look off-center relative to the visible content. Pass the outlet
   * context's `mainInsetLeft`; defaults to 0 (centers on the full viewport).
   */
  insetLeft?: number;
  children: ReactNode;
};

/**
 * Brand modal — wraps `@mui/material/Dialog` (Portal, focus trap,
 * escape-key handling, backdrop click all delivered by MUI) inside our
 * popup-utility visual: 4px radius, shadow-soft, grey-900-24 backdrop,
 * 32px (or 24px) inner padding, IntroHeader pattern with optional icon
 * block. Consumer API is identical to the prior hand-rolled Modal.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  iconName,
  width = 480,
  padding = 32,
  insetLeft = 0,
  children,
}: Props) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-label={title}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "#4e59663d" }, // grey-900-24
        },
        // MUI centers the paper within this container via flex; narrowing its
        // box to [insetLeft, viewport-right] re-centers within the visible
        // content area instead of the full viewport. The backdrop (a sibling)
        // is untouched, so it still dims the sidebar too.
        container: {
          sx: { position: "absolute", top: 0, bottom: 0, left: insetLeft, right: 0 },
        },
        paper: {
          sx: {
            width,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 32px)",
            margin: 0,
            padding: `${padding}px`,
            backgroundColor: "#ffffff",
            border: "1px solid #e5ebf1", // grey-200
            borderRadius: "4px",
            boxShadow: "0px 8px 32px #2d343c14", // shadow-soft
            overflow: "auto",
          },
        },
      }}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          {iconName && (
            <div className="flex size-9 shrink-0 items-center justify-center rounded-[4px] border-t border-grey-900 bg-grey-950 text-grey-300 shadow-flat-black">
              <Icon name={iconName} size={20} />
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <h2 className="text-h4 text-black">{title}</h2>
            {description && <p className="text-small text-grey-900">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex size-6 items-center justify-center text-grey-900 hover:text-black"
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        {children}
      </div>
    </Dialog>
  );
}
